<template>
  <div class="creation-form">
    <section class="cm-dialog" @click="closeOptionDropdown">
      <div class="cm-body">
        <label
          v-if="!file"
          class="cm-dropzone"
          :class="{ 'is-dragging': isDragging }"
          @dragover.prevent="handleDragOver"
          @dragleave.prevent="handleDragLeave"
          @dragenter.prevent="handleDragEnter"
          @drop.prevent="handleDrop"
        >
          <span class="cm-icon i-upload"></span>
          <p class="cm-dropzone-title">{{ isDragging ? $t('audioUploader.dragText') : $t('audioUploader.clickText') }}</p>
          <p class="cm-dropzone-hint">{{ $t('audioUploader.supportedFormats') }} · MP3 / MP4 / WAV</p>
          <input
            ref="fileInput"
            type="file"
            accept="audio/*,video/*"
            class="cm-file-input"
            @change="uploadFile"
          >
        </label>

        <div v-else class="cm-file">
          <div class="cm-file-main">
            <span class="cm-file-name">{{ file.name }}</span>
            <span class="cm-file-meta">
              <span>{{ formatFileSize(file.size) }}</span>
              <span v-if="fileDuration">{{ formatDuration(fileDuration) }}</span>
            </span>
          </div>
          <button type="button" class="cm-icon-button is-small" :title="$t('common.delete')" :aria-label="$t('common.delete')" @click.stop="removeFile">
            <span class="cm-icon i-close"></span>
          </button>
        </div>

        <PlanLimitNotice v-if="limit" kind="file" :minutes="limit.minutes" :limit-minutes="limit.limitMinutes" />
        <p v-else-if="error" class="cm-error">{{ error }}</p>

        <div class="cm-group">
          <div class="cm-field">
            <span class="cm-label">{{ $t('audioUploader.providerLabel') }}</span>
            <div class="cm-segmented" role="radiogroup" :aria-label="$t('audioUploader.providerLabel')">
              <button
                v-for="provider in transcriptionProviders"
                :key="provider.value"
                type="button"
                role="radio"
                :class="{ active: provider.value === selectedTranscriptionProvider }"
                :aria-checked="provider.value === selectedTranscriptionProvider"
                :disabled="isGenerating"
                @click="selectTranscriptionProvider(provider.value)"
              >
                {{ $t('audioUploader.providers.' + provider.labelKey) }}
              </button>
            </div>
            <p class="cm-hint">{{ $t('audioUploader.providerHint') }}</p>
          </div>

          <div class="cm-field">
            <label class="cm-label" for="transcription-language">{{ $t('audioUploader.languageLabel') }}</label>
            <div :class="['cm-select', { open: openOptionDropdown === 'language' }]" @click.stop>
              <button
                id="transcription-language"
                type="button"
                class="cm-select-button"
                :disabled="isGenerating"
                :aria-expanded="openOptionDropdown === 'language'"
                aria-haspopup="listbox"
                @click="toggleOptionDropdown('language')"
              >
                <span class="cm-select-value">{{ selectedLanguageLabel }}</span>
                <span class="cm-select-arrow"></span>
              </button>
              <div v-if="openOptionDropdown === 'language'" class="cm-select-menu is-scroll" role="listbox">
                <button
                  v-for="language in availableTranscriptionLanguages"
                  :key="language.value"
                  type="button"
                  :class="['cm-select-option', { selected: language.value === selectedTranscriptionLanguage }]"
                  role="option"
                  :aria-selected="language.value === selectedTranscriptionLanguage"
                  @click="selectTranscriptionLanguage(language.value)"
                >
                  <span>{{ $t('audioUploader.languages.' + language.labelKey) }}</span>
                  <span class="cm-select-check"></span>
                </button>
              </div>
            </div>
            <p class="cm-hint">{{ $t('audioUploader.languageHint') }}</p>
          </div>
        </div>

        <div v-if="isConverting || uploadProgress !== null" class="cm-progress" role="status" aria-live="polite">
          <div class="cm-progress-head">
            <span>{{ isConverting ? $t('audioUploader.converting') : $t('audioUploader.uploading') }}</span>
            <strong v-if="!isConverting">{{ uploadProgress }}%</strong>
          </div>
          <div
            class="cm-progress-track"
            :class="{ 'is-indeterminate': isConverting }"
            role="progressbar"
            :aria-valuenow="isConverting ? undefined : uploadProgress"
            aria-valuemin="0"
            aria-valuemax="100"
          >
            <span :style="isConverting ? undefined : { width: `${uploadProgress}%` }"></span>
          </div>
          <p class="cm-progress-note">{{ $t('audioUploader.uploadNextStep') }}</p>
        </div>
      </div>

      <footer class="cm-footer">
        <span :class="['cm-footer-meta', { 'is-insufficient': isInsufficientUsage }]">
          <template v-if="fileDuration">
            {{ $t('audioUploader.usageCost', { cost: calculateUsageCost(fileDuration) }) }}
            <template v-if="isInsufficientUsage"> · {{ $t('audioUploader.insufficientUsage') }}</template>
          </template>
        </span>
        <button
          type="button"
          class="cm-button-primary"
          :disabled="!file || isGenerating"
          :aria-busy="isGenerating"
          @click.stop="generateSubtitles"
        >
          <span v-if="isGenerating" class="cm-spinner" aria-hidden="true"></span>
          {{ isGenerating ? $t('audioUploader.generatingSubtitles') : $t('common.generate') }}
        </button>
      </footer>
    </section>
  </div>
</template>

<script>
import { useModals } from '@/composables/modals'
import { auth } from '@/firebase'
import { useAuth } from '@/composables/auth';
import { formatDuration as _formatDuration, formatFileSize as _formatFileSize } from '@/utils/format';
import { apiPost } from '@/composables/useApi';
import PlanLimitNotice from './PlanLimitNotice.vue';
import {
  getAvailableTranscriptionLanguages,
  getDefaultTranscriptionLanguage,
  transcriptionLanguages,
  transcriptionProviders,
} from '@/utils/transcriptionOptions';

export default {
  name: 'AudioUploader',
  components: { PlanLimitNotice },
  setup() {
    const { userDetails } = useAuth();
    return {
      userDetails
    };
  },
  data() {
    return {
      isDragging: false,
      dragCounter: 0,
      file: null,
      fileDuration: null,
      // 文件超出当前方案的时长上限时显示升级提示
      limit: null,
      isGenerating: false,
      isConverting: false,
      uploadProgress: null,
      error: null,
      openOptionDropdown: null,
      selectedTranscriptionProvider: 'deepgram',
      transcriptionProviders,
      selectedTranscriptionLanguage: 'en',
      transcriptionLanguages,
    }
  },
  watch: {
    selectedTranscriptionProvider(provider) {
      this.selectedTranscriptionLanguage = getDefaultTranscriptionLanguage(provider)
    }
  },
  computed: {
    availableTranscriptionLanguages() {
      return getAvailableTranscriptionLanguages(this.selectedTranscriptionProvider);
    },
    selectedLanguageLabel() {
      const language = this.transcriptionLanguages.find(item => item.value === this.selectedTranscriptionLanguage);
      return language ? this.$t('audioUploader.languages.' + language.labelKey) : '';
    },
    planContentLimits() {
      if (this.userDetails?.isPremium) {
        return { maxDuration: 3600, maxFileSizeMb: 200 };
      }
      if (this.userDetails?.isPro) {
        return { maxDuration: 1800, maxFileSizeMb: 100 };
      }
      return { maxDuration: 1200, maxFileSizeMb: 50 };
    },
    maxDuration() {
      return this.planContentLimits.maxDuration;
    },
    maxFileSizeMb() {
      return this.planContentLimits.maxFileSizeMb;
    },
    hasEnoughUsage() {
      if (!this.fileDuration || !this.userDetails) return false;
      const requiredUsage = this.calculateUsageCost(this.fileDuration);
      return this.userDetails.remainingUsage >= requiredUsage;
    },
    isInsufficientUsage() {
      return this.fileDuration && !this.hasEnoughUsage;
    }
  },
  methods: {
    formatFileSize(bytes) {
      return _formatFileSize(bytes)
    },
    formatDuration(seconds) {
      return _formatDuration(seconds)
    },
    calculateUsageCost(duration) {
      // 每6秒消耗1额度，向上取整
      return Math.ceil(duration / 6);
    },
    toggleOptionDropdown(name) {
      if (this.isGenerating) return;
      this.openOptionDropdown = this.openOptionDropdown === name ? null : name;
    },
    closeOptionDropdown() {
      this.openOptionDropdown = null;
    },
    selectTranscriptionProvider(provider) {
      this.selectedTranscriptionProvider = provider;
      this.closeOptionDropdown();
    },
    selectTranscriptionLanguage(language) {
      this.selectedTranscriptionLanguage = language;
      this.closeOptionDropdown();
    },
    handleDragEnter(event) {
      event.preventDefault();
      event.stopPropagation();
      this.dragCounter++;
      this.isDragging = true;
    },
    handleDragLeave(event) {
      event.preventDefault();
      event.stopPropagation();
      this.dragCounter--;
      if (this.dragCounter === 0) {
        this.isDragging = false;
      }
    },
    handleDragOver(event) {
      event.preventDefault();
      event.stopPropagation();
    },
    triggerFileInput() {
      if (!this.isGenerating) {
        this.$refs.fileInput.click();
      }
    },
    removeFile() {
      this.file = null;
      this.fileDuration = null;  // 清除时长
      this.uploadProgress = null;
      this.error = null;
      this.limit = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    async uploadFile(event) {
      const file = event.target.files[0];
      if (file) {
        this.error = null;
        this.limit = null;
        try {
          // 检查文件类型
          if (!file.type.startsWith('audio/') && !file.type.startsWith('video/')) {
            this.error = this.$t('audioUploader.invalidFile');
            return;
          }

          const maxSize = this.maxFileSizeMb * 1024 * 1024;
          if (file.size > maxSize) {
            this.error = this.$t('audioUploader.fileTooLarge', { maxSize: `${this.maxFileSizeMb}MB` });
            return;
          }

          const duration = await this.getFileDuration(file);
        
          if (duration > this.maxDuration) {
            this.limit = { minutes: Math.ceil(duration / 60), limitMinutes: this.maxDuration / 60 };
            return;
          }
          
          this.file = file;
          this.fileDuration = duration; // 保存文件时长
          this.error = null;
        } catch (error) {
          this.error = this.$t('audioUploader.durationLimit', {
            maxDuration: this.maxDuration / 60
          });
        } finally {
          this.isGenerating = false;
        }
      }
    },

    getFileDuration(file) {
      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const media = file.type.startsWith('video/') ? document.createElement('video') : document.createElement('audio');
        
        media.addEventListener('loadedmetadata', () => {
          URL.revokeObjectURL(url);
          resolve(media.duration);
        });
        
        media.addEventListener('error', () => {
          URL.revokeObjectURL(url);
          reject(new Error('无法读取文件时长'));
        });
        
        media.preload = 'metadata';
        media.src = url;
      });
    },
        
    handleDrop(event) {
      event.preventDefault();
      event.stopPropagation();
      this.isDragging = false;
      this.dragCounter = 0;
      const file = event.dataTransfer.files[0];
      if (file) {
        this.uploadFile({ target: { files: [file] } });
      }
    },
    
    async generateSubtitles() {
      if (!this.file || this.isGenerating) return;
      if (!auth.currentUser) { useModals().showLoginModal(); return }
      if (this.isInsufficientUsage) { useModals().handleUpgrade(); return }

      this.isGenerating = true;
      
      try {
        // 1. 上传文件
        const uploadResult = await this.saveFileToServer(this.file);
        
        // 2. 创建文件记录
        const fileRecord = {
          name: uploadResult.name,
          filePath: uploadResult.filePath,
          status: 'processing',
          uploadedTime: new Date().toISOString(),
          fileType: uploadResult.contentType,
          duration: uploadResult.duration,
          transcriptionProvider: this.selectedTranscriptionProvider,
          transcriptionLanguage: this.selectedTranscriptionLanguage
        };

        const recordResult = await apiPost('/api/files', fileRecord);

        // 3. 调用转录接口
        const transcribeData = {
          fileId: recordResult.id,
          transcriptionProvider: this.selectedTranscriptionProvider,
          transcriptionLanguage: this.selectedTranscriptionLanguage
        };

        await apiPost('/api/transcribe', transcribeData);
        this.$emit('file-generated');
      } catch (error) {
        this.error = error.message;
        this.$emit('error', error.message);
      } finally {
        this.isGenerating = false;
      }
    },

    async saveFileToServer(file) {
      if (this.supportsDirectUpload(file)) {
        return this.saveFileDirectlyToStorage(file);
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('fileSize', file.size);
      formData.append('duration', this.fileDuration);

      return apiPost('/api/upload', formData);
    },

    supportsDirectUpload(file) {
      const directTypes = new Set([
        'audio/flac',
        'audio/mp3',
        'audio/mp4',
        'audio/mpeg',
        'audio/wav',
        'audio/webm',
        'audio/x-m4a',
        'audio/x-wav'
      ]);
      return directTypes.has((file.type || '').split(';', 1)[0].toLowerCase());
    },

    async saveFileDirectlyToStorage(file) {
      this.uploadProgress = 0;
      let session = null;
      try {
        session = await apiPost('/api/uploads/resumable', {
          fileName: file.name,
          fileSize: file.size,
          contentType: file.type,
          duration: this.fileDuration
        });

        await this.uploadToResumableSession(session.uploadUrl, file);
        return await apiPost('/api/uploads/complete', {
          filePath: session.filePath,
          fileName: file.name
        });
      } catch (error) {
        if (session?.filePath) {
          try {
            await apiPost('/api/uploads/cancel', {
              filePath: session.filePath
            });
          } catch (cleanupError) {
            console.warn('Unable to clean up incomplete upload', cleanupError);
          }
        }
        throw error;
      } finally {
        this.uploadProgress = null;
      }
    },

    uploadToResumableSession(uploadUrl, file) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl, true);
        xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable && event.total > 0) {
            this.uploadProgress = Math.min(
              100,
              Math.round((event.loaded / event.total) * 100)
            );
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            this.uploadProgress = 100;
            resolve();
            return;
          }
          reject(new Error(`Storage upload failed (HTTP ${xhr.status})`));
        };
        xhr.onerror = () => reject(new Error('Storage upload failed'));
        xhr.onabort = () => reject(new Error('Storage upload was cancelled'));
        xhr.send(file);
      });
    }
  }
}
</script>
